import { useState } from 'react';
import { isValidSolanaAddress } from '../lib/utils';

interface Props {
  onSearch: (address: string) => void;
  onWalletSearch?: (address: string) => void;
  loading?: boolean;
  onQueryChange?: (query: string) => void;
}

type SearchType = 'token' | 'wallet' | 'unknown';

export function TokenSearch({ onSearch, onWalletSearch, loading, onQueryChange }: Props) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const detectSearchType = (value: string): { type: SearchType; address?: string } => {
    const trimmed = value.trim();

    // Check for URLs first
    const address = extractAddress(trimmed);

    // If it looks like a Solana address
    if (isValidSolanaAddress(address)) {
      // Heuristic: wallet addresses often start with specific patterns
      // For now, we treat all valid addresses as tokens unless user specifies
      return { type: 'token', address };
    }

    // If the original input was a URL but extraction failed
    if (trimmed.includes('pump.fun') || trimmed.includes('raydium') || trimmed.includes('dexscreener')) {
      return { type: 'unknown' };
    }

    // Otherwise it's a text search (name/symbol)
    return { type: 'unknown' };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = input.trim();
    if (!trimmed) return;

    const { type, address } = detectSearchType(trimmed);

    if (type === 'token' && address) {
      setError(null);
      onSearch(address);
    } else if (type === 'wallet' && address && onWalletSearch) {
      setError(null);
      onWalletSearch(address);
    } else if (trimmed.length >= 2) {
      // It's a text search - let the parent handle via onQueryChange
      // If no results, show error
      setError(null);
    } else {
      setError('Enter a token name, address, or URL');
    }
  };

  const handleChange = (value: string) => {
    setInput(value);
    setError(null);
    onQueryChange?.(value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ position: 'relative' }}>
        <SearchIcon />
        <input
          type="text"
          className="input"
          placeholder="Search token name, symbol, or paste address/URL..."
          value={input}
          onChange={(e) => handleChange(e.target.value)}
          style={{
            paddingLeft: '40px',
            paddingRight: '80px',
            borderColor: error ? 'var(--error)' : undefined,
          }}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="btn btn-primary"
          style={{
            position: 'absolute',
            right: '4px',
            top: '4px',
            bottom: '4px',
            padding: '0 14px',
            minHeight: 'auto',
          }}
        >
          {loading ? (
            <span className="animate-spin" style={{
              display: 'inline-block',
              width: '14px',
              height: '14px',
              border: '2px solid rgba(0,0,0,0.2)',
              borderTopColor: '#000',
              borderRadius: '50%',
            }} />
          ) : (
            'GO'
          )}
        </button>
      </div>
      {error && (
        <p style={{ fontSize: '12px', color: 'var(--error)', marginTop: '6px' }}>
          {error}
        </p>
      )}
    </form>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-tertiary)"
      strokeWidth="2"
      style={{
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function extractAddress(input: string): string {
  // Match pump.fun URLs
  const pumpMatch = input.match(/pump\.fun\/(?:coin\/)?([A-Za-z0-9]{32,44})/);
  if (pumpMatch) return pumpMatch[1];

  // Match letsbonk.fun URLs
  const bonkMatch = input.match(/letsbonk\.fun\/(?:token\/)?([A-Za-z0-9]{32,44})/);
  if (bonkMatch) return bonkMatch[1];

  // Match dexscreener URLs
  const dexMatch = input.match(/dexscreener\.com\/solana\/([A-Za-z0-9]{32,44})/);
  if (dexMatch) return dexMatch[1];

  // Match raydium URLs
  const rayMatch = input.match(/raydium\.io\/.*?([A-Za-z0-9]{32,44})/);
  if (rayMatch) return rayMatch[1];

  // Match birdeye URLs
  const birdMatch = input.match(/birdeye\.so\/token\/([A-Za-z0-9]{32,44})/);
  if (birdMatch) return birdMatch[1];

  // Return as-is (might be raw address)
  return input;
}
